import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  Bars3Icon,
  BellIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  HomeIcon,
  ShieldCheckIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { getAuth, signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { Theme } from "react-daisyui";
import { useNavigate } from "react-router-dom";
import HeadPng from "../assets/head.png";
import Ball from "../assets/logo512.png";
import AppLogo from "../assets/PeranaSportsLogo.png";
import { useAuthStatus } from "../hooks/useAuthStatus";
import Matches from "./Matches";
import Players from "./Players";
import PlaylistsPage from "./Playlists";
import Settings from "./Settings";
import Teams from "./Teams";
// import Reports from "./Reports";
// import SearchAthlete from "./SearchAthlete";

const navigation = [
  { name: "Matches", index: 0, icon: HomeIcon, current: true },
  { name: "Players", index: 1, icon: UserIcon, current: false },
  { name: "Teams", index: 2, icon: ShieldCheckIcon, current: false },
  { name: "Playlists", index: 3, icon: ChartBarIcon, current: false },
  // { name: "Reports", index: 4, icon: ChartBarIcon, current: false },
  // { name: "Search", index: 5, icon: MagnifyingGlassCircleIcon, current: false },
  // { name: "Settings", index: 6, icon: Cog6ToothIcon, current: false },
];
const teams = [
  { id: 1, name: "Heroicons", index: 10, initial: "H", current: false },
  { id: 2, name: "Tailwind Labs", index: 11, initial: "T", current: false },
  { id: 3, name: "Workcation", index: 12, initial: "W", current: false },
];
const userNavigation = [
  { name: "Your profile", index: 90 },
  { name: "Sign out", index: 99 },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function FrontPage() {
  const navigate = useNavigate();
  const { firebaseUser } = useAuthStatus();
  const [currentContent, setCurrentContentRaw] = useState(() =>
    Number(sessionStorage.getItem("frontpage_tab") ?? 0),
  );
  const setCurrentContent = (idx) => {
    sessionStorage.setItem("frontpage_tab", String(idx));
    setCurrentContentRaw(idx);
  };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [, forceUpdate] = useState(0);
  const [currentTheme, setCurrentTheme] = useState(
    localStorage.getItem("theme") ? localStorage.getItem("theme") : "garden",
  );

  const showContents = () => {
    if (currentContent === 0) return <Matches />;
    else if (currentContent === 1) return <Players />;
    else if (currentContent === 2) return <Teams />;
    else if (currentContent === 3) return <PlaylistsPage />;
    else if (currentContent === 6)
      return <Settings onThemeChange={doThemeChange} />;
    return <></>;
  };

  const handleSignOut = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      localStorage.removeItem("currentUser");
      navigate("/sign-in");
    } catch (error) {
      console.error("Error signing out:", error);
      alert("Error signing out. Please try again.");
    }
  };

  const handleUserNavClick = (item) => {
    if (item.name === "Sign out") {
      handleSignOut();
    } else if (item.name === "Your profile") {
      setCurrentContent(90);
    }
  };

  const doSelectTheme = () => {
    setCurrentContent(6);
  };

  const doThemeChange = (theme) => {
    setCurrentTheme(theme);
  };

  useEffect(() => {
    for (let i = 0; i < navigation.length; i++) {
      navigation[i].current = i === currentContent;
    }
    // Keep <html> in sync with the selected theme so the page background
    // behind the app margins recolours immediately (no refresh needed).
    document.documentElement.setAttribute("data-theme", currentTheme);
    // loadData();
    // console.log("Current Content: ", currentContent, currentTheme);
    forceUpdate((n) => !n);
  }, [currentContent, currentTheme]);

  return (
    <>
      <Theme dataTheme={currentTheme}>
        <div className="h-full bg-base-100">
          <Dialog
            open={sidebarOpen}
            onClose={setSidebarOpen}
            className="relative z-50 lg:hidden"
          >
            <DialogBackdrop
              transition
              className="fixed inset-0 bg-neutral/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
            />

            <div className="fixed inset-0 flex">
              <DialogPanel
                transition
                className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
              >
                <TransitionChild>
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(false)}
                      className="-m-2.5 p-2.5"
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        aria-hidden="true"
                        className="size-6 text-white"
                      />
                    </button>
                  </div>
                </TransitionChild>
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-base-100 px-6 pb-4">
                  <nav className="flex flex-1 flex-col mt-20">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" className="-mx-2 space-y-1">
                          {navigation.map((item) => (
                            <li
                              key={item.name}
                              onClick={() => setCurrentContent(item.index)}
                              className="cursor-pointer"
                            >
                              <a
                                className={classNames(
                                  item.current
                                    ? "bg-base-200 text-primary"
                                    : "text-base-content/80 hover:bg-base-200 hover:text-primary",
                                  "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                                )}
                              >
                                <item.icon
                                  aria-hidden="true"
                                  className={classNames(
                                    item.current
                                      ? "text-primary"
                                      : "text-base-content/50 group-hover:text-primary",
                                    "size-6 shrink-0",
                                  )}
                                />
                                {item.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </li>
                      {/* <li>
                      <div className="text-xs/6 font-semibold text-base-content/50">
                        Your teams
                      </div>
                      <ul role="list" className="-mx-2 mt-2 space-y-1">
                        {teams.map((team) => (
                          <li key={team.name}>
                            <a
                              href={team.href}
                              className={classNames(
                                team.current
                                  ? "bg-base-200 text-primary"
                                  : "text-base-content/80 hover:bg-base-200 hover:text-primary",
                                "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                              )}
                            >
                              <span
                                className={classNames(
                                  team.current
                                    ? "border-primary text-primary"
                                    : "border-base-300 text-base-content/50 group-hover:border-primary group-hover:text-primary",
                                  "flex size-6 shrink-0 items-center justify-center rounded-lg border bg-base-100 text-[0.625rem] font-medium"
                                )}
                              >
                                {team.initial}
                              </span>
                              <span className="truncate">{team.name}</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </li> */}
                      <li className="mt-auto">
                        <a
                          // href="#"
                          onClick={() => doSelectTheme()}
                          className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-base-content/80 hover:bg-base-200 hover:text-primary"
                        >
                          <Cog6ToothIcon
                            aria-hidden="true"
                            className="size-6 shrink-0 text-base-content/50 group-hover:text-primary"
                          />
                          Settings
                        </a>
                      </li>
                    </ul>
                  </nav>
                  <div className="flex p-4 shrink-0 items-center">
                    <img alt="STARS" src={AppLogo} className="w-auto" />
                  </div>
                </div>
              </DialogPanel>
            </div>
          </Dialog>

          {/* Static sidebar for desktop */}
          <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
            {/* Sidebar component, swap this element with another sidebar if you like */}
            <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-base-300 bg-base-100 px-6 pb-4">
              <nav className="flex flex-1 flex-col mt-20">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2 space-y-1">
                      {navigation.map((item) => (
                        <li
                          key={item.name}
                          onClick={() => setCurrentContent(item.index)}
                          className="cursor-pointer"
                        >
                          <a
                            className={classNames(
                              item.current
                                ? "bg-base-200 text-primary"
                                : "text-base-content/80 hover:bg-base-200 hover:text-primary",
                              "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                            )}
                          >
                            <item.icon
                              aria-hidden="true"
                              className={classNames(
                                item.current
                                  ? "text-primary"
                                  : "text-base-content/50 group-hover:text-primary",
                                "size-6 shrink-0",
                              )}
                            />
                            {item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                  {/* <li>
                  <div className="text-xs/6 font-semibold text-base-content/50">
                    Your teams
                  </div>
                  <ul role="list" className="-mx-2 mt-2 space-y-1">
                    {teams.map((team) => (
                      <li key={team.name}>
                        <a
                          href={team.href}
                          className={classNames(
                            team.current
                              ? "bg-base-200 text-primary"
                              : "text-base-content/80 hover:bg-base-200 hover:text-primary",
                            "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                          )}
                        >
                          <span
                            className={classNames(
                              team.current
                                ? "border-primary text-primary"
                                : "border-base-300 text-base-content/50 group-hover:border-primary group-hover:text-primary",
                              "flex size-6 shrink-0 items-center justify-center rounded-lg border bg-base-100 text-[0.625rem] font-medium"
                            )}
                          >
                            {team.initial}
                          </span>
                          <span className="truncate">{team.name}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li> */}
                  <li className="mt-auto">
                    <a
                      // href="#"
                      onClick={() => doSelectTheme()}
                      className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-base-content/80 hover:bg-base-200 hover:text-primary"
                    >
                      <Cog6ToothIcon
                        aria-hidden="true"
                        className="size-6 shrink-0 text-base-content/50 group-hover:text-primary"
                      />
                      Settings
                    </a>
                  </li>
                </ul>
              </nav>
              <div className="flex p-4 shrink-0 items-center">
                <img alt="STARS" src={AppLogo} className="w-auto" />
              </div>
            </div>
          </div>

          <div className="lg:pl-72">
            <div className="sticky top-0 z-40 lg:mx-auto lg:max-w-7xl lg:px-8">
              <div className="flex h-16 items-center gap-x-4 border-b border-base-300 bg-base-100 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-0 lg:shadow-none">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(true)}
                  className="-m-2.5 p-2.5 text-base-content/80 lg:hidden"
                >
                  <span className="sr-only">Open sidebar</span>
                  <Bars3Icon aria-hidden="true" className="size-6" />
                </button>

                {/* Separator */}
                <div
                  aria-hidden="true"
                  className="h-6 w-px bg-base-300 lg:hidden"
                />

                <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                  <form
                    action="#"
                    method="GET"
                    className="grid flex-1 grid-cols-1"
                  >
                    <div className="flex mt-2">
                      <img alt="STARS" src={Ball} className="size-8 mt-2 mr-2" />
                      <h1 className="font-bold mt-2 text-lg">
                        TennisStats Pro
                      </h1>
                    </div>
                    {/* <input
                    name="search"
                    type="search"
                    placeholder="Search"
                    aria-label="Search"
                    className="col-start-1 row-start-1 block size-full bg-base-100 pl-8 text-base text-base-content outline-none placeholder:text-base-content/50 sm:text-sm/6"
                  />
                  <MagnifyingGlassIcon
                    aria-hidden="true"
                    className="pointer-events-none col-start-1 row-start-1 size-5 self-center text-base-content/50"
                  /> */}
                  </form>
                  <div className="flex items-center gap-x-4 lg:gap-x-6">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5 text-base-content/50 hover:text-base-content/70"
                    >
                      <span className="sr-only">View notifications</span>
                      <BellIcon aria-hidden="true" className="size-6" />
                    </button>

                    {/* Separator */}
                    <div
                      aria-hidden="true"
                      className="hidden lg:block lg:h-6 lg:w-px lg:bg-base-300"
                    />

                    {/* Profile dropdown */}
                    <Menu as="div" className="relative">
                      <MenuButton className="-m-1.5 flex items-center p-1.5">
                        <span className="sr-only">Open user menu</span>
                        <img
                          alt=""
                          src={HeadPng}
                          className="size-8 rounded-full bg-base-200"
                        />
                        <span className="hidden lg:flex lg:items-center">
                          <span
                            aria-hidden="true"
                            className="ml-4 text-sm/6 font-semibold text-base-content"
                          >
                            {firebaseUser?.displayName}
                          </span>
                          <ChevronDownIcon
                            aria-hidden="true"
                            className="ml-2 size-5 text-base-content/50"
                          />
                        </span>
                      </MenuButton>
                      <MenuItems
                        transition
                        className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-base-100 py-2 shadow-lg ring-1 ring-base-content/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                      >
                        {userNavigation.map((item) => (
                          <MenuItem key={item.name}>
                            <a
                              onClick={() => handleUserNavClick(item)}
                              className="block px-3 py-1 text-sm/6 text-base-content data-[focus]:bg-base-200 data-[focus]:outline-none"
                            >
                              {item.name}
                            </a>
                          </MenuItem>
                        ))}
                      </MenuItems>
                    </Menu>
                  </div>
                </div>
              </div>
            </div>

            <main className="py-4">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {showContents(currentContent)}
              </div>
            </main>
          </div>
        </div>
      </Theme>
    </>
  );
}
